
import { Helmet } from "react-helmet";
import Layout from "@/components/Layout";
import { lazy, Suspense, startTransition } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

// Lazy load the image component
const LazyImage = lazy(() => import("@/components/ui/lazy-image"));

const About = () => {
  // Organization schema markup
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "TopMotorPart",
    "description": "Global leader in automotive parts export with 25+ years of industry expertise",
    "foundingDate": "1998",
    "url": "https://topmotorpart.com",
    "sameAs": [
      "https://linkedin.com/company/topmotorpart",
      "https://facebook.com/topmotorpart"
    ]
  };

  return (
    <Layout>
      <Helmet>
        <title>About TopMotorPart | Trusted Auto Parts Export Partner</title>
        <meta 
          name="description" 
          content="Discover 25+ years of expertise in global automotive parts export. Learn about our quality-focused mission, industry certifications, and reliable international distribution network."
        />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-center mb-12">
          <Suspense fallback={<div className="h-40 w-40 bg-gray-200 animate-pulse rounded-md"></div>}>
            <LazyImage
              src="/lovable-uploads/20ef2a99-a4fa-4b11-945d-114aa6b7a7fa.png"
              alt="TopMotorPart Logo"
              className="h-40 w-auto"
            />
          </Suspense>
        </div>

        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-8">
            Your Trusted Global Partner in Automotive Excellence
          </h1>
          <p className="text-lg text-gray-600 mb-12">
          TopMotorPart represents the future of automotive parts export, combining German engineering excellence with a customer-first approach. Backed by extensive industry knowledge and state-of-the-art quality control systems, we're committed to becoming your trusted partner in the global aftermarket automotive sector. Our ISO-certified facility and rigorous testing protocols ensure every part meets international standards, setting new benchmarks for reliability and performance.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-primary">
              Global Reach & Expertise
            </h2>
            <p className="text-gray-600">
              At TopMotorPart, we export high-quality aftermarket auto parts globally, catering to brands like Toyota and BMW. With precision engineering and efficient logistics, we deliver reliable solutions tailored to diverse automotive needs.
            </p>
          </div>
          
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-primary">
              Quality Assurance
            </h2>
            <p className="text-gray-600">
              At TopMotorPart, quality is our priority. Every product undergoes rigorous testing to meet international standards, ensuring durability, performance, and seamless compatibility with global vehicle brands for reliable aftermarket solutions.
            </p>
          </div>

          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-primary">
              Sustainable Solutions
            </h2>
            <p className="text-gray-600">
              TopMotorPart promotes sustainability by offering durable aftermarket parts that extend vehicle lifespans, reduce waste, and support eco-friendly practices, ensuring a positive impact on both customers and the environment worldwide.
            </p>
          </div>

          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-primary">
              Customer Success
            </h2>
            <p className="text-gray-600">
              At TopMotorPart, customer success drives us. We deliver tailored solutions, reliable support, and high-quality products to meet diverse needs, ensuring satisfaction and long-term partnerships with clients across global markets.
            </p>
          </div>
        </div>

        <div className="text-center">
          <Button size="lg" asChild>
            <Link to="/contact">Inquire Now</Link>
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default About;
